import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock, ShieldCheck, FileCheck2, Clock, Users, Globe2, MessageSquare, BadgeCheck } from 'lucide-react';
import SEO, { siteUrl } from '../components/SEO';
import { CompaniesOfficeTrustStrip } from '../components/CompaniesOfficeBlock';
import { TrustChip } from '../components/TrustChip';

// =============================================================================
// GIWAHS — Founding Partner Landing Page (review-only)
// Source of truth: /app/memory/founder_vault/32_GIWAHS_Founder_Partner_Landing_Page.md
// Doc 09: copy-only build. No payment activation, no Stripe, no live backend.
// Placeholder allocation counters are visually labelled as such.
// =============================================================================

const GOLD = '#9A7B2A';

// Allocation placeholders – matching Doc 32 §2.1 / §2.11. Replace with live RPC
// data only when MEMBERSHIPS_ENABLED=true and /api/memberships/availability ships.
const ALLOCATION_SEED = {
  gold:       { total: 300, claimed: 297, remaining: 3,  waitlist: 12 },
  elite:      { total: 150, claimed: 148, remaining: 2,  waitlist: 28 },
  enterprise: { total: 50,  claimed: 50,  remaining: 0,  waitlist: 17 },
};

const TIERS = [
  {
    key: 'gold',
    name: 'Gold Founding Partner',
    designation: 'Gold Founder #001–#300',
    annual: 490,
    standard: 990,
    annualSaving: 500,
    tenYearSaving: 5000,
    seats: 1,
    cap: 300,
    badge: null,
    fit: 'Solo operators, founders, single-buyer brands.',
    includes: [
      'Verified Supplier Profile (where applicable)',
      'RFQ participation',
      'Founder badge + Founder Member Number',
      '10-year price-lock guarantee',
      'Founders Wall recognition',
      'Quarterly product-roadmap briefing',
    ],
  },
  {
    key: 'elite',
    name: 'Elite Founding Partner',
    designation: 'Elite Founder #001–#150',
    annual: 1490,
    standard: 2990,
    annualSaving: 1500,
    tenYearSaving: 15000,
    seats: 5,
    cap: 150,
    badge: 'Most chosen',
    fit: 'Teams of 3–10 with regular RFQ flow and multi-certification verification needs.',
    includes: [
      'Everything in Gold',
      'Team workspace + multi-user roles',
      'Expanded RFQ throughput',
      'Priority verification queue',
      'Founder Council invitation',
    ],
  },
  {
    key: 'enterprise',
    name: 'Enterprise Founding Partner',
    designation: 'Enterprise Founder #001–#050',
    annual: 4990,
    standard: 7400,
    annualSaving: 2410,
    tenYearSaving: 24100,
    seats: 25,
    cap: 50,
    badge: null,
    fit: 'Multi-team organisations, dedicated sourcing teams, 50+ RFQs/year, custom reporting needs.',
    includes: [
      'Everything in Elite',
      'Dedicated success manager',
      'Advance API preview access',
      'Named-account verification reviewer',
      'On-call onboarding session',
    ],
  },
];

const fmt = (n) => `$${n.toLocaleString('en-US')}`;

const TierCard = ({ tier }) => {
  const alloc = ALLOCATION_SEED[tier.key];
  const soldOut = alloc.remaining <= 0;
  return (
    <div
      data-testid={`tier-card-${tier.key}`}
      className="card-dark flex flex-col p-6 md:p-8 text-platinum"
    >
      {tier.badge && (
        <div className="mb-4 inline-flex w-fit giwahs-chip-dark label-overline">
          {tier.badge}
        </div>
      )}
      <div className="label-overline tracking-wider">{tier.name}</div>
      <div className="mt-1 font-mono-data text-[11px] tracking-[0.16em] uppercase" style={{ color: GOLD }}>{tier.designation}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <div
          className="font-display tracking-tighter"
          style={{ fontWeight: 700, fontSize: 'clamp(2.5rem,5vw,3.25rem)', lineHeight: 1 }}
        >
          {fmt(tier.annual)}
        </div>
        <div className="text-sm text-neutral-600">USD / year</div>
      </div>
      <div className="mt-2 text-sm text-neutral-500">
        Standard (2027+): <span className="line-through">{fmt(tier.standard)}</span>
      </div>
      <div className="mt-1 text-sm" style={{ color: GOLD }}>
        Locked saving · {fmt(tier.annualSaving)}/yr · {fmt(tier.tenYearSaving)} over 10 years
      </div>

      <div className="mt-5 flex items-center gap-3 border-y border-neutral-200 py-3 text-sm">
        <Users size={16} strokeWidth={1.5} />
        <span>{tier.key === 'gold' ? 'Solo workspace' : tier.key === 'elite' ? 'Team workspace' : 'Multi-team workspace'}</span>
        <span className="text-neutral-300">·</span>
        <Globe2 size={16} strokeWidth={1.5} />
        <span>Worldwide-limited</span>
      </div>

      <div className="mt-4 text-sm text-neutral-700">{tier.fit}</div>

      <ul className="mt-5 space-y-2 text-sm text-neutral-600">
        {tier.includes.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <BadgeCheck size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 border-t border-neutral-200 pt-4 text-xs uppercase tracking-wider text-neutral-500 font-mono">
        Applications open · review-based allocation
      </div>

      <Link
        to="/founding-partners/apply"
        state={{ tier: tier.key }}
        data-testid={`tier-apply-${tier.key}`}
        className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-medium transition ${
          soldOut
            ? 'border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-50'
            : 'bg-neutral-900 text-white hover:bg-black'
        }`}
      >
        {soldOut ? 'Join the waitlist' : `Apply for ${tier.name.split(' ')[0]}`}
        <ArrowUpRight size={16} strokeWidth={1.5} />
      </Link>
    </div>
  );
};

const FoundingPartners = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Founding Partners', url: '/founding-partners' },
  ];
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'GIWAHS Founding Partner Programme',
    description:
      'A worldwide-limited annual subscription with a contractual 10-year price-lock. Capped at 500 organisations across Gold, Elite, and Enterprise tiers.',
    brand: { '@type': 'Brand', name: 'GIWAHS' },
    offers: TIERS.map((t) => ({
      '@type': 'Offer',
      name: t.name,
      price: t.annual,
      priceCurrency: 'USD',
      availability:
        ALLOCATION_SEED[t.key].remaining > 0
          ? 'https://schema.org/LimitedAvailability'
          : 'https://schema.org/OutOfStock',
      url: `${siteUrl}/founding-partners`,
    })),
  };

  return (
    <div data-testid="founding-partners-page" className="surface-obsidian text-platinum">
      <SEO
        title="Founding Partner Programme — Lock your price for ten years"
        description="Become a GIWAHS Founding Partner. 500 organisations worldwide, three tiers (Gold $490 · Elite $1,490 · Enterprise $4,990 annual USD), contractually protected 10-year price-lock. Founder Member Number, Founders Wall, quarterly roadmap briefings."
        keywords="GIWAHS Founding Partner, sourcing membership, factory verification, 10-year price lock, founder member number"
        jsonLd={productJsonLd}
        breadcrumbs={breadcrumbs}
      />

      {/* 1. Announcement bar (Doc 32 §2.1) */}
      <div
        data-testid="announcement-bar"
        className="border-b border-hairline bg-[#0A0A0A]"
      >
        <div className="mx-auto max-w-[1440px] px-5 py-3 md:px-8">
          <p className="text-xs text-neutral-700 md:text-sm">
            <span className="label-overline tracking-wider">/ Limited worldwide</span>
            &nbsp;·&nbsp;Founding Partner positions for a small group of global leaders.&nbsp;
            <span className="text-neutral-500">
              Gold · Elite · Enterprise — applications open.
            </span>
          </p>
        </div>
      </div>

      {/* 2. Hero (Doc 32 §2.2) */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-16 md:grid-cols-12 md:gap-12 md:px-8 md:py-24">
          <div className="md:col-span-8 min-w-0">
            <span className="label-overline">/ Founding Partner Programme</span>
            <h1
              className="mt-6 font-display tracking-tighter"
              style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 6rem)', lineHeight: 0.94 }}
            >
              The price you pay today<br />
              is the price you'll pay<br />
              <span style={{ color: GOLD }}>for 10 years</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
              Become a GIWAHS Founding Partner and lock your membership rate for 10 years
              from activation — while we build the world's most trusted manufacturing and
              product intelligence network.
            </p>

            {/* D-057 / D-058 — Independently Verifiable trust strip */}
            <CompaniesOfficeTrustStrip className="mt-8" />

            {/* D-080 — public TrustChips show trust/status only · founder numbering retained internally */}
            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="hero-founder-chip-row">
              <TrustChip
                state="founding-partner"
                variant="solid"
                size="md"
                testId="hero-founding-partner-chip"
              />
              <TrustChip
                state="trusted"
                variant="solid"
                size="md"
                testId="hero-trusted-chip"
              />
              <TrustChip
                state="verified"
                variant="solid"
                size="md"
                testId="hero-verified-chip"
              />
            </div>
            <span className="mt-3 inline-block font-mono-data text-[11px] uppercase tracking-[0.16em] text-neutral-500">
              Founding Partner · Trusted · Verified
            </span>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/founding-partners/apply"
                data-testid="hero-apply-cta"
                className="btn-giwahs btn-giwahs-dark"
              >
                Apply for a Founding Partnership <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-call-cta"
                className="inline-flex items-center gap-2 border-b border-neutral-900 pb-1 text-sm font-medium hover:gap-3 transition-all"
              >
                Book a 20-minute call with the founder <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <p className="mt-6 text-xs uppercase tracking-wider text-neutral-500 font-mono">
              USD subscription · Stripe-secured · 14-day full refund · Cancel anytime
            </p>
          </div>
          <div className="md:col-span-4 min-w-0">
            <div className="border border-neutral-900 bg-[#0A0A0A] p-6 text-white">
              <span className="label-overline text-neutral-400">/ Signed by the founder</span>
              <p className="mt-5 text-sm leading-relaxed text-neutral-300">
                Every Founding Partner contract is signed personally. The 10-year price-lock is
                a clause, not a promise.
              </p>
              <div className="mt-6 border-t border-neutral-800 pt-5">
                <div
                  className="font-display tracking-tight"
                  style={{ fontWeight: 600, fontSize: '1.25rem' }}
                >
                  Jay Elkington
                </div>
                <div className="mt-1 text-xs text-neutral-400">Founder &amp; Director</div>
                <div className="text-xs text-neutral-500 font-mono mt-2">Whaitiri Black Limited · trading as GIWAHS</div>
                <div className="text-xs text-neutral-500 font-mono mt-0.5">Auckland · New Zealand</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Why now (Doc 32 §2.3) */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline">/ 01 — Why now</span>
            <h2
              className="mt-4 font-display tracking-tighter"
              style={{ fontWeight: 600, fontSize: 'clamp(2rem,4vw,3rem)' }}
            >
              Most platforms inflate prices. GIWAHS will too — for everyone except Founding Partners.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-base leading-relaxed text-neutral-700 md:text-lg">
            <p>
              Every credible B2B platform you use today was cheapest in its first year. Slack,
              Notion, Figma — the operators who joined early paid 50–80% less than the operators
              who joined in year five.
            </p>
            <p>
              GIWAHS is doing the opposite of the usual pricing trajectory: we publish the
              long-term rate today, tell you exactly what you'll pay in year ten, and lock it in
              writing.
            </p>
            <p>
              That commitment is only possible if we cap the programme. A worldwide-limited group of Founding Partners — once allocated, the programme closes permanently. There is no "Founding Partner Wave 2" next year.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Tiers (Doc 32 §2.4) */}
      <section id="tiers" className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="label-overline">/ 02 — What you get</span>
              <h2
                className="mt-4 font-display tracking-tighter"
                style={{ fontWeight: 600, fontSize: 'clamp(2rem,4vw,3rem)' }}
              >
                Three tiers. One window.
              </h2>
            </div>
            <p className="max-w-md text-sm text-neutral-600">
              All Founder prices in USD, exclusive of applicable taxes. Stripe processes
              payment. 14-day full-refund cooling-off applies. See{' '}
              <Link to="/trust" className="underline">Trust Centre</Link>.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TIERS.map((t) => <TierCard key={t.key} tier={t} />)}
          </div>
        </div>
      </section>

      {/* 5. 10-year math (Doc 32 §2.5) */}
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-400">/ 03 — The 10-Year Founder Price-Lock</span>
          <h2
            className="mt-4 max-w-3xl font-display tracking-tighter"
            style={{ fontWeight: 600, fontSize: 'clamp(2rem,4vw,3rem)' }}
          >
            We wrote the lock into the contract — not the marketing.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-300 md:text-lg">
            Your Founding Partner subscription price is contractually locked for 10 years from
            the date your membership is activated. If our standard pricing changes during that
            period, your Founding Partner rate remains unchanged. The lock is a clause in our
            Founding Partner Terms, recorded against your Stripe subscription with{' '}
            <code className="bg-neutral-800 px-1.5 py-0.5 text-xs font-mono">
              founder_price_lock_years=10
            </code>{' '}
            metadata, and enforced automatically every renewal.
          </p>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-700 text-neutral-400">
                  <th className="py-3 text-left label-overline tracking-wider">Year</th>
                  <th className="py-3 text-right label-overline tracking-wider">Standard (projected)</th>
                  <th className="py-3 text-right label-overline tracking-wider">Founder (locked)</th>
                  <th className="py-3 text-right label-overline tracking-wider">Your saving</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { y: 'Year 1 from activation', s: 990, f: 490 },
                  { y: 'Year 3', s: 1080, f: 490 },
                  { y: 'Year 5', s: 1180, f: 490 },
                  { y: 'Year 8', s: 1360, f: 490 },
                  { y: 'Year 10', s: 1560, f: 490 },
                ].map((r) => (
                  <tr key={r.y} className="border-b border-neutral-800">
                    <td className="py-3">{r.y}</td>
                    <td className="py-3 text-right text-neutral-400">{fmt(r.s)}</td>
                    <td className="py-3 text-right text-white">{fmt(r.f)}</td>
                    <td className="py-3 text-right" style={{ color: GOLD }}>
                      {fmt(r.s - r.f)}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="py-4">10-year total (Gold)</td>
                  <td className="py-4 text-right text-neutral-400">{fmt(11800)}</td>
                  <td className="py-4 text-right">{fmt(4900)}</td>
                  <td className="py-4 text-right" style={{ color: GOLD }}>{fmt(6900)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-6 max-w-3xl text-xs text-neutral-500 font-mono">
            Standard projection assumes a conservative 3% annual increase; actual standard rate
            may rise faster. Founder price is locked and contractually protected for 10 years.
          </p>
        </div>
      </section>

      {/* 6. Founding Partner benefits — 4 cards (Doc 32 §2.6) */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 04 — Founding Partner status</span>
          <h2
            className="mt-4 max-w-3xl font-display tracking-tighter"
            style={{ fontWeight: 600, fontSize: 'clamp(2rem,4vw,3rem)' }}
          >
            What "Founding Partner" actually means.
          </h2>
          <div className="mt-12 grid gap-px bg-black md:grid-cols-2 lg:grid-cols-4">
            {[
              { i: BadgeCheck, t: 'Founder Member Number', d: 'Permanent, sequential identifier (FP-0001…). Issued once your subscription is active and onboarding is complete. Yours for as long as you remain active.' },
              { i: ShieldCheck, t: 'Founder badge', d: 'Visible on your supplier or buyer profile across the GIWAHS Platform. Other users see immediately that you backed the network before it scaled.' },
              { i: Users, t: 'Founders Wall', d: 'A permanent public recognition page at /founders, ordered by Founder Member Number. Activates once 25 active founders are acquired.' },
              { i: MessageSquare, t: 'Roadmap briefings', d: 'Quarterly written briefings on what we\'re building next, before it ships. Verified Founder feedback from ongoing use of the platform helps inform future priorities.' },
            ].map((b, i) => (
              <div key={i} className="bg-white p-7">
                <b.i size={22} strokeWidth={1.5} />
                <div className="mt-6 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>
                  {b.t}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. How GIWAHS is different (Doc 32 §2.7) */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 05 — How GIWAHS is different</span>
          <h2
            className="mt-4 max-w-3xl font-display tracking-tighter"
            style={{ fontWeight: 600, fontSize: 'clamp(2rem,4vw,3rem)' }}
          >
            GIWAHS was created to become the trust layer of global sourcing.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
            Built on verification, intelligence, transparency, and long-term relationships —
            here is how the models compare.
          </p>
          <div className="mt-12 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse border border-black bg-white text-sm">
              <thead>
                <tr className="bg-neutral-100">
                  <th className="border border-black p-4 text-left label-overline">Dimension</th>
                  <th className="border border-black p-4 text-left label-overline">Directories</th>
                  <th className="border border-black p-4 text-left label-overline">Marketplaces</th>
                  <th className="border border-black bg-neutral-900 p-4 text-left label-overline text-white">GIWAHS</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Ranking model', 'Typically paid placement', 'Typically feature fees', 'Verification-first ranking'],
                  ['Verification', 'Usually self-reported', 'Varies by platform', 'Multi-document, audited'],
                  ['Relationships', 'Lead-based introductions', 'Held within the marketplace', 'Direct buyer–supplier relationships'],
                  ['Revenue model', 'Lead fees + advertising', 'Transaction fees', 'Annual membership only'],
                  ['Founder programme', 'Not offered', 'Not offered', '500 partners, 10-year price-lock'],
                ].map((row) => (
                  <tr key={row[0]}>
                    <td className="border border-black p-4 font-medium">{row[0]}</td>
                    <td className="border border-black p-4 text-neutral-600">{row[1]}</td>
                    <td className="border border-black p-4 text-neutral-600">{row[2]}</td>
                    <td className="border border-black bg-neutral-50 p-4 font-medium">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 8. Trust & compliance (Doc 32 §2.9) */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="label-overline">/ 06 — Trust & compliance</span>
              <h2 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 600, fontSize: 'clamp(2rem,4vw,3rem)' }}>
                Founder Partners commit for ten years. We treat that like the bond it is.
              </h2>
              <Link
                to="/trust"
                data-testid="landing-trust-link"
                className="mt-8 inline-flex items-center gap-2 border-b border-neutral-900 pb-1 label-overline hover:gap-3 transition-all"
              >
                Read the full Trust Centre <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="md:col-span-7 grid gap-px bg-black sm:grid-cols-3">
              {[
                { i: Lock, t: 'Bank-grade infrastructure', d: 'Stripe payments, Supabase + AWS data, Cloudflare edge, TLS 1.2+, AES-256 at rest.' },
                { i: FileCheck2, t: 'Privacy-first by design', d: 'GDPR · UK GDPR · CCPA · NZ Privacy Act 2020 · AU Privacy Act compliant. Full DPA available.' },
                { i: ShieldCheck, t: 'No data resale, ever', d: 'Your RFQs, supplier profiles, and certifications are yours. We never sell user data.' },
              ].map((b, i) => (
                <div key={i} className="bg-white p-6">
                  <b.i size={20} strokeWidth={1.5} />
                  <div className="mt-5 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{b.t}</div>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600">{b.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 9. Allocation status (Doc 32 §2.11) */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 07 — Allocation status</span>
          <h2 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 600, fontSize: 'clamp(2rem,4vw,3rem)' }}>
            Founding allocation ledger.
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-neutral-600">
            Counters below are illustrative reference figures until the membership API is activated.
            When live, they refresh every 60 seconds from the GIWAHS admin RPC.
          </p>
          <div className="mt-10 overflow-x-auto">
            <table data-testid="landing-allocation-table" className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-900 text-neutral-500">
                  <th className="py-3 text-left label-overline tracking-wider">Tier</th>
                  <th className="py-3 text-right label-overline tracking-wider">Total</th>
                  <th className="py-3 text-right label-overline tracking-wider">Allocated</th>
                  <th className="py-3 text-right label-overline tracking-wider">Remaining</th>
                  <th className="py-3 text-right label-overline tracking-wider">Waitlist</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(ALLOCATION_SEED).map(([k, v]) => (
                  <tr key={k} className="border-b border-neutral-200">
                    <td className="py-4 font-medium capitalize">{k}</td>
                    <td className="py-4 text-right">{v.total}</td>
                    <td className="py-4 text-right">{v.claimed}</td>
                    <td className="py-4 text-right" style={{ color: v.remaining > 0 ? GOLD : '#888' }}>
                      <strong>{v.remaining}</strong>
                    </td>
                    <td className="py-4 text-right text-neutral-500">{v.waitlist}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 10. Final CTA (Doc 32 §2.13) */}
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-20 text-center md:px-8 md:py-28">
          <h2
            className="mx-auto max-w-4xl font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2.25rem,5vw,4rem)', lineHeight: 0.95 }}
          >
            A worldwide-limited window.<br />
            <span style={{ color: GOLD }}>Lock your price for a decade.</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/founding-partners/apply"
              data-testid="final-apply-cta"
              className="btn-giwahs btn-giwahs-primary"
            >
              Apply for a Founding Partnership <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <Link
              to="/contact"
              data-testid="final-call-cta"
              className="btn-giwahs btn-giwahs-outline-gold"
            >
              Book a 20-minute call <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
          <p className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 font-mono">
            <Clock size={12} strokeWidth={1.5} />
            Review mode · checkout activation pending founder approval
          </p>
        </div>
      </section>
    </div>
  );
};

export default FoundingPartners;
